import {
  ChangeDetectionStrategy,
  Component,
  input,
  signal,
} from '@angular/core';
import { CmsNavigationComponent, CmsNavigationNode } from '@boels/models';
import { CommonModule } from '@angular/common';
import {
  faChevronDown,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { VerticalAlignerComponent } from '../vertical-aligner/vertical-aligner.component';
import { DelayedHoverDirective, QaHooksDirective } from '@boels/helpers';
import { BoelsSkeletonComponent } from '../skeleton/boels-skeleton.component';
import { CmsRouterLinkDirective } from '@boels/shared-state';

@Component({
  selector: 'boels-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FontAwesomeModule,
    VerticalAlignerComponent,
    DelayedHoverDirective,
    BoelsSkeletonComponent,
    QaHooksDirective,
    CmsRouterLinkDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavigationComponent {
  nav = input<CmsNavigationComponent | undefined>();
  isSubMenuOpen = signal<boolean>(false);
  isChildSubMenuOpen = signal<boolean>(false);
  activeHoveredItem = signal<CmsNavigationNode | undefined>(undefined);
  protected readonly faChevronDown = faChevronDown;
  protected readonly faChevronRight = faChevronRight;
  protected readonly nodeChildren = signal<CmsNavigationNode | undefined>(
    undefined,
  );
  nodeChildrenFocus = signal<
    { originalFocus: HTMLElement; firstToFocus: HTMLElement } | undefined
  >(undefined);

  toggleSubmenu(event: Event, handle: 'click' | 'hover'): void {
    if (!this.isSubMenuOpen()) {
      this.openSubmenu(event, handle);
    } else {
      this.closeSubmenu(event, handle);
    }
  }

  openSubmenu(event: Event, handle: 'click' | 'hover'): void {
    this.closeChildSubmenu();

    this.handleElementsAttributes(event, 'open', handle);

    event.stopImmediatePropagation();
    event.stopPropagation();

    this.isSubMenuOpen.set(true);
  }

  closeSubmenu(event: Event, handle: 'click' | 'hover'): void {
    this.closeChildSubmenu();
    this.handleElementsAttributes(event, 'close', handle);
    this.isSubMenuOpen.set(false);
  }

  closeAllMenus(): void {
    if (this.isChildSubMenuOpen() || !this.isSubMenuOpen()) return;
    const elements = document.querySelectorAll(
      '.boels-navigation__main-menu__item.-has-submenu',
    );
    elements.forEach((element) => element.classList.remove('-submenu-open'));
    this.closeChildSubmenu();
    this.isSubMenuOpen.set(false);
  }

  handleElementsAttributes(
    event: Event,
    action: 'open' | 'close',
    handle: 'click' | 'hover',
  ) {
    const submenuOpenClass = '-submenu-open';
    let parentElement: HTMLElement;
    let anchorElement: HTMLElement;

    if (handle === 'click') {
      anchorElement = event.target as HTMLElement;
      parentElement = anchorElement.parentElement as HTMLElement;
    } else {
      parentElement = event.target as HTMLElement;
      anchorElement = parentElement.querySelector(
        '.boels-navigation__main-menu__anchor',
      ) as HTMLElement;
    }

    if (action === 'open') {
      parentElement.classList.add(submenuOpenClass);
      anchorElement.setAttribute('aria-expanded', 'true');
    }

    if (action === 'close') {
      parentElement.classList.remove(submenuOpenClass);
      anchorElement.setAttribute('aria-expanded', 'false');
    }
  }

  toggleChildSubmenu(event: Event, node: CmsNavigationNode): void {
    if (this.isChildSubMenuOpen()) {
      this.closeChildSubmenu();
    } else {
      this.openChildSubmenu(event, node);
    }
  }

  openChildSubmenu(event: Event, node: CmsNavigationNode): void {
    this.nodeChildren.set(node);
    this.activeHoveredItem.set(node);
    this.storeSubmenuFocusElements(event);
    this.isChildSubMenuOpen.set(true);
  }

  closeChildSubmenu() {
    this.nodeChildren.set(undefined);
    this.nodeChildrenFocus.set(undefined);
    this.isChildSubMenuOpen.set(false);
    this.activeHoveredItem.set(undefined);
  }

  goToChildSubmenuItem(event: Event) {
    if (this.nodeChildren()) {
      event.preventDefault();
      this.nodeChildrenFocus()?.firstToFocus.focus();
    }
  }

  handleChildSubmenuEvents(
    event: KeyboardEvent,
    index: number | null,
    isLast = false,
  ) {
    if (
      isLast &&
      event.code == 'Tab' &&
      !(event.shiftKey && event.code == 'Tab')
    ) {
      this.nodeChildrenFocus()?.originalFocus.focus();
      this.closeChildSubmenu();
    }

    if (event.code == 'Escape') {
      event.preventDefault();
      this.nodeChildrenFocus()?.originalFocus.focus();
      this.closeChildSubmenu();
    }

    if (event.shiftKey && event.code == 'Tab' && index === 0) {
      event.preventDefault();
      this.nodeChildrenFocus()?.originalFocus.focus();
    }
  }

  storeSubmenuFocusElements(event: Event) {
    const delay = (ms: number) =>
      new Promise((resolve) => setTimeout(resolve, ms));

    const findElementToFocus = async (event: Event) => {
      const retryLimit = 100;
      const delayMs = 400; // Delay in milliseconds
      let attempts = 0;
      let elementToFocus: HTMLElement | null = null;

      while (!elementToFocus && attempts < retryLimit) {
        const parentElement = document.querySelector(
          '.boels-navigation__submenu__children__container',
        ) as HTMLElement;

        elementToFocus = parentElement.querySelector(
          '.boels-navigation__submenu__children__category-title',
        ) as HTMLElement | null;

        if (!elementToFocus) {
          await delay(delayMs);
          attempts++;
        }
      }

      if (elementToFocus) {
        this.nodeChildrenFocus.set({
          originalFocus: event.target as HTMLElement,
          firstToFocus: elementToFocus,
        });
      } else {
        console.log('Child element not found after retries');
      }
    };

    findElementToFocus(event);
  }
}
